import type { MonthStats, SamePeriodStats, StatsDto, YearStats } from '@flightlog/shared';
import { useState, type CSSProperties } from 'react';
import { plural } from '../format';
import { haptic } from '../telegram';

const EARTH_KM = 40_075;
const MOON_KM = 384_400;

const MONTH_LETTERS = ['Я', 'Ф', 'М', 'А', 'М', 'И', 'И', 'А', 'С', 'О', 'Н', 'Д'];
const MONTHS_GENITIVE = [
  'янв',
  'фев',
  'мар',
  'апр',
  'мая',
  'июн',
  'июл',
  'авг',
  'сен',
  'окт',
  'ноя',
  'дек',
];
export const MONTHS_FULL = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

function decimal(value: number): string {
  return value.toLocaleString('ru-RU', { maximumFractionDigits: value < 10 ? 1 : 0 });
}

/** А: километры и часы в понятных сравнениях. */
export function Comparisons({ stats }: { stats: StatsDto }) {
  const km = stats.totalDistanceKm;
  const days = stats.totalDurationMin / 1440;
  const approx = stats.estimatedFlights > 0 ? '≈ ' : '';

  return (
    <section className="section">
      <h2 className="section-title">Если сложить</h2>
      <div className="compare">
        <div className="compare-card">
          <b>{decimal(km / EARTH_KM)}×</b>
          <span>вокруг Земли по экватору</span>
        </div>
        <div className="compare-card">
          {km < MOON_KM ? (
            <>
              <b>{Math.round((km / MOON_KM) * 100)}%</b>
              <span>пути до Луны</span>
            </>
          ) : (
            <>
              <b>{decimal(km / MOON_KM)}×</b>
              <span>до Луны</span>
            </>
          )}
        </div>
        <div className="compare-card">
          <b>
            {approx}
            {decimal(days)}
          </b>
          <span>{plural(Math.floor(days), ['сутки', 'суток', 'суток'])} в воздухе</span>
        </div>
      </div>
    </section>
  );
}

type Metric = 'flights' | 'km' | 'hours';

const METRIC_LABEL: Record<Metric, string> = { flights: 'рейсы', km: 'км', hours: 'часы' };

function metricValue(item: { flights: number; distanceKm: number; durationMin: number }, metric: Metric) {
  if (metric === 'km') return item.distanceKm;
  if (metric === 'hours') return item.durationMin / 60;
  return item.flights;
}

/** Километры при большом разбросе — все в тысячах, чтобы подписи были одного вида. */
function shortValue(value: number, metric: Metric, max: number): string {
  if (metric === 'km' && max >= 10_000) return `${decimal(value / 1000)}к`;
  return Math.round(value).toLocaleString('ru-RU');
}

/** Все годы подряд до текущего, пустые — нулями, чтобы на оси не было дыр. */
function continuousYears(byYear: YearStats[], currentYear: number): YearStats[] {
  if (byYear.length === 0) return [];
  const known = new Map(byYear.map((item) => [item.year, item]));
  const first = Math.min(...known.keys());
  const last = Math.max(currentYear, ...known.keys());
  const result: YearStats[] = [];
  for (let year = first; year <= last; year += 1) {
    result.push(known.get(year) ?? { year, flights: 0, distanceKm: 0, durationMin: 0 });
  }
  return result;
}

function periodText(period: SamePeriodStats, metric: Metric): string | null {
  const current = metricValue(period.current, metric);
  const previous = metricValue(period.previous, metric);
  if (current === 0 && previous === 0) return null;

  const [month, day] = period.until.split('-').map(Number);
  const date = `${day} ${MONTHS_GENITIVE[(month ?? 1) - 1]}`;
  const unit =
    metric === 'flights'
      ? (n: number) => plural(n, ['рейс', 'рейса', 'рейсов'])
      : metric === 'km'
        ? () => 'км'
        : (n: number) => plural(n, ['час', 'часа', 'часов']);

  const now = Math.round(current);
  const before = Math.round(previous);
  const head = `${period.year} по ${date}: ${now.toLocaleString('ru-RU')} ${unit(now)}`;
  if (before === 0) return `${head}. В ${period.year - 1} к этой дате полётов не было.`;
  const diff = now - before;
  if (diff === 0) return `${head} — столько же, сколько к этой дате в ${period.year - 1}.`;
  const abs = Math.abs(diff);
  return `${head} — на ${abs.toLocaleString('ru-RU')} ${unit(abs)} ${
    diff > 0 ? 'больше' : 'меньше'
  }, чем к этой дате в ${period.year - 1}.`;
}

/** Б: столбики по годам с переключателем «рейсы / км / часы». */
export function YearChart({ stats }: { stats: StatsDto }) {
  const [metric, setMetric] = useState<Metric>('flights');
  const years = continuousYears(stats.byYear, stats.samePeriod.year);
  if (years.length === 0) return null;

  const values = years.map((item) => metricValue(item, metric));
  const max = Math.max(1, ...values);

  const width = 340;
  const height = 170;
  const top = 18;
  const bottom = 20;
  const plot = height - top - bottom;
  const slot = width / years.length;
  const barWidth = Math.min(36, slot * 0.66);
  const shortYears = years.length > 7;
  const note = periodText(stats.samePeriod, metric);

  return (
    <section className="section">
      <div className="section-head">
        <h2 className="section-title">По годам</h2>
        <div className="segmented" role="tablist">
          {(Object.keys(METRIC_LABEL) as Metric[]).map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={metric === key}
              className={metric === key ? 'is-active' : ''}
              onClick={() => {
                haptic();
                setMetric(key);
              }}
            >
              {METRIC_LABEL[key]}
            </button>
          ))}
        </div>
      </div>
      <div className="chart-card">
        <svg viewBox={`0 0 ${width} ${height}`} className="year-chart" role="img" aria-label="Полёты по годам">
          <line x1="0" x2={width} y1={top + plot} y2={top + plot} className="chart-axis" />
          {years.map((item, index) => {
            const value = values[index]!;
            const barHeight = value > 0 ? Math.max(2, (value / max) * plot) : 0;
            const x = index * slot + (slot - barWidth) / 2;
            const y = top + plot - barHeight;
            const current = item.year === stats.samePeriod.year;
            return (
              <g key={item.year}>
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={barHeight}
                  rx={4}
                  className={current ? 'chart-bar chart-bar-current' : 'chart-bar'}
                />
                {value > 0 ? (
                  <text x={x + barWidth / 2} y={y - 5} className="chart-value">
                    {shortValue(value, metric, max)}
                  </text>
                ) : null}
                <text
                  x={index * slot + slot / 2}
                  y={height - 5}
                  className={current ? 'chart-label chart-label-current' : 'chart-label'}
                >
                  {shortYears ? `’${String(item.year).slice(2)}` : item.year}
                </text>
              </g>
            );
          })}
        </svg>
        {note ? <p className="chart-note">{note}</p> : null}
      </div>
    </section>
  );
}

/** В: сетка «годы × месяцы»; нажатие на месяц открывает его рейсы. */
export function MonthGrid({
  byMonth,
  onOpen,
}: {
  byMonth: MonthStats[];
  onOpen: (month: string) => void;
}) {
  if (byMonth.length === 0) return null;
  const counts = new Map(byMonth.map((item) => [item.month, item.flights]));
  const max = Math.max(1, ...byMonth.map((item) => item.flights));
  const yearsWithFlights = byMonth.map((item) => Number(item.month.slice(0, 4)));
  const first = Math.min(...yearsWithFlights);
  const last = Math.max(...yearsWithFlights);
  const years: number[] = [];
  for (let year = last; year >= first; year -= 1) years.push(year);

  return (
    <section className="section">
      <div className="section-head">
        <h2 className="section-title">По месяцам</h2>
        <span className="section-note">нажми на месяц — покажу рейсы</span>
      </div>
      <div className="chart-card">
        <div className="heat">
          <span />
          {MONTH_LETTERS.map((letter, index) => (
            <span key={index} className="heat-head">
              {letter}
            </span>
          ))}
          {years.map((year) => (
            <HeatRow key={year} year={year} counts={counts} max={max} onOpen={onOpen} />
          ))}
        </div>
        <div className="heat-legend">
          <span>меньше</span>
          {[0.25, 0.5, 0.75, 1].map((level) => (
            <i key={level} className="heat-swatch" style={{ '--level': level } as CSSProperties} />
          ))}
          <span>больше</span>
        </div>
      </div>
    </section>
  );
}

function HeatRow({
  year,
  counts,
  max,
  onOpen,
}: {
  year: number;
  counts: Map<string, number>;
  max: number;
  onOpen: (month: string) => void;
}) {
  return (
    <>
      <span className="heat-year">{year}</span>
      {MONTH_LETTERS.map((_, index) => {
        const month = `${year}-${String(index + 1).padStart(2, '0')}`;
        const count = counts.get(month) ?? 0;
        if (count === 0) return <span key={month} className="heat-cell" />;
        const level = 0.25 + 0.75 * (count / max);
        return (
          <button
            key={month}
            type="button"
            className={level > 0.6 ? 'heat-cell heat-strong' : 'heat-cell'}
            style={{ '--level': level } as CSSProperties}
            aria-label={`${MONTHS_FULL[index]} ${year}: ${count}`}
            onClick={() => {
              haptic();
              onOpen(month);
            }}
          >
            {count}
          </button>
        );
      })}
    </>
  );
}
